import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextareaAutosize,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  IconButton as MuiIconButton,
  Typography,
  Box,
  Chip,
  Tooltip,
} from "@material-ui/core";
import {
  Close,
  NoteAdd,
  Edit,
  Visibility,
  VisibilityOff,
  AttachFile,
  GetApp,
  Image,
  PictureAsPdf,
  Description,
  TableChart,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

// Require Editor JS files.
import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/third_party/embedly.min.js";
// Require Editor CSS files.
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/third_party/embedly.min.css";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import FroalaEditor from "react-froala-wysiwyg";

import { contactsTypesActions } from "../../../../../store/contactTypes/contactTypesActions";
import { launchesActions } from "../../../../../store/launches/launchesActions";

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: "6px",
    maxWidth: "800px",
    width: "100%",
    maxHeight: "90vh",
    minHeight: "600px",
    margin: "32px auto",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    fontSize: "22px",
    color: "#fff",
    padding: "15px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#3699FF",
    margin: 0,
    position: "relative",
    minHeight: "70px",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
    lineHeight: 1.2,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "22px",
    color: "#fff",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 600,
    margin: 0,
    flex: 1,
    paddingRight: "40px",
    lineHeight: 1.2,
  },
  closeButton: {
    position: "absolute",
    top: "50%",
    right: "15px",
    transform: "translateY(-50%)",
    color: "white",
    padding: "8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  dialogContent: {
    padding: "0 !important",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  contentWrapper: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    gap: "16px",
    minHeight: 0,
  },
  scrollableBody: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    paddingRight: "8px",

    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c1c1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#a8a8a8",
    },
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    flexShrink: 0,
  },
  addButton: {
    width: "auto",
    alignSelf: "flex-start",
    flexShrink: 0,
    marginBottom: "8px",
  },
  formContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    backgroundColor: "#f9f9f9",
    flexShrink: 0,
    scrollMarginTop: "20px",
  },
  formTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "15px",
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "10px",
  },
  textArea: {
    width: "100%",
    minHeight: "120px",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    resize: "vertical",
    fontFamily: "inherit",
    "&:focus": {
      outline: "none",
      borderColor: "#3699ff",
    },
  },
  formGrid: {
    marginBottom: "15px",
  },
  formField: {
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  dateField: {
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "16px",
    flexShrink: 0,
    paddingTop: "16px",
    borderTop: "1px solid #e0e0e0",
  },
  interactionsContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: "200px",
  },
  interactionItem: {
    padding: "15px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    marginBottom: "10px",
    backgroundColor: "#f9f9f9",
    position: "relative",
  },
  interactionContent: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "8px",
    whiteSpace: "pre-wrap",
  },
  truncatedContent: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    cursor: "pointer",
  },
  interactionMeta: {
    fontSize: "12px",
    color: "#666",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  interactionType: {
    display: "inline-block",
    padding: "2px 8px",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  },
  interactionActions: {
    position: "absolute",
    top: "10px",
    right: "10px",
    display: "flex",
    gap: "5px",
    zIndex: 1,
  },
  actionButton: {
    padding: "4px",
    minWidth: "auto",
  },
  emptyState: {
    textAlign: "center",
    color: "#666",
    padding: "40px 20px",
    fontSize: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    flex: 1,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 20px",
    height: "100%",
    flex: 1,
  },
  cancelButton: {
    textTransform: "none !important",
  },
  emailPreview: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "8px",
    cursor: "pointer",
    borderRadius: "4px",
    padding: "8px",
  },
  expandedEmail: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "8px",
    padding: "10px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    border: "1px solid #ddd",
  },
  threadContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "8px",
  },
  threadItem: {
    padding: "12px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    border: "1px solid #e0e0e0",
    position: "relative",
  },
  threadHeader: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "8px",
    paddingBottom: "4px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  threadContent: {
    fontSize: "14px",
    color: "#333",
    "& blockquote": {
      borderLeft: "4px solid #ccc",
      margin: "10px 0",
      paddingLeft: "10px",
      color: "#555",
      fontStyle: "italic",
    },
  },
  attachmentsContainer: {
    marginTop: "12px",
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    border: "1px dashed #ccc",
  },
  attachmentsTitle: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#666",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  attachmentsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  attachmentChip: {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#e3f2fd",
    },
  },
  attachmentLink: {
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  downloadButton: {
    marginLeft: "4px",
    padding: "2px",
  },
  imageAttachmentsContainer: {
    marginTop: "12px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: "12px",
  },
  imageAttachmentItem: {
    position: "relative",
    borderRadius: "4px",
    overflow: "hidden",
    border: "1px solid #e0e0e0",
    cursor: "pointer",
    "&:hover": {
      "& $imageOverlay": {
        opacity: 1,
      },
    },
  },
  imagePreview: {
    width: "100%",
    height: "120px",
    objectFit: "cover",
    display: "block",
  },
  imageOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    opacity: 0,
    transition: "opacity 0.2s ease",
  },
  imageActionButton: {
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  imageModal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    cursor: "pointer",
  },
  imageModalContent: {
    maxWidth: "90vw",
    maxHeight: "90vh",
    objectFit: "contain",
  },
}));

const INTERACTION_TYPES = [
  { value: 1, label: "Call" },
  { value: 2, label: "Email" },
  { value: 3, label: "Visit" },
];

const InteractionHistoryModal = ({ open, setOpen, prospectData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const scrollableBodyRef = useRef(null);
  const formContainerRef = useRef(null);

  const [interactions, setInteractions] = useState([]);
  const [showAddInteraction, setShowAddInteraction] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [interactionContent, setInteractionContent] = useState("");
  const [interactionType, setInteractionType] = useState(1);
  const [interactionDate, setInteractionDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  var test = "";

  const getFileIcon = url => {
    const extension = url?.split(".").pop().toLowerCase();

    if (extension?.match(/(jpg|jpeg|png|gif|bmp|svg|webp)/)) {
      return <Image fontSize='small' />;
    } else if (extension === "pdf") {
      return <PictureAsPdf fontSize='small' />;
    } else if (extension?.match(/(xls|xlsx|csv)/)) {
      return <TableChart fontSize='small' />;
    } else if (extension?.match(/(doc|docx|txt)/)) {
      return <Description fontSize='small' />;
    }
    return <AttachFile fontSize='small' />;
  };

  const getFileNameFromUrl = url => {
    try {
      const urlParts = url?.split("/");
      const fileName = urlParts[urlParts.length - 1];
      if (fileName && fileName.length > 20) {
        return "attachment." + (url?.split(".").pop() || "file");
      }
      return fileName || "attachment";
    } catch (error) {
      return "attachment";
    }
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName || getFileNameFromUrl(url);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download attachment");
    }
  };

  const renderAttachments = attachments => {
    if (!attachments || attachments.length === 0) return null;

    const imageAttachments = attachments.filter(url => {
      const extension = url?.split(".").pop().toLowerCase();
      return extension?.match(/(jpg|jpeg|png|gif|bmp|svg|webp)/);
    });

    const otherAttachments = attachments.filter(url => {
      const extension = url?.split(".").pop().toLowerCase();
      return !extension?.match(/(jpg|jpeg|png|gif|bmp|svg|webp)/);
    });

    return (
      <div className={classes.attachmentsContainer}>
        {imageAttachments.length > 0 && (
          <>
            <div className={classes.attachmentsTitle}>
              <Image fontSize='small' />
              Images ({imageAttachments.length})
            </div>
            <div className={classes.imageAttachmentsContainer}>
              {imageAttachments.map((url, index) => {
                const fileName = getFileNameFromUrl(url);
                return (
                  <div
                    key={index}
                    className={classes.imageAttachmentItem}
                    onClick={() => setPreviewImage(url)}>
                    <img
                      src={url}
                      alt={`Attachment ${index + 1}`}
                      className={classes.imagePreview}
                      onError={e => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://via.placeholder.com/150?text=Image+Error";
                      }}
                    />
                    <div className={classes.imageOverlay}>
                      <Tooltip title='View'>
                        <IconButton
                          size='small'
                          className={classes.imageActionButton}
                          onClick={e => {
                            e.stopPropagation();
                            setPreviewImage(url);
                          }}>
                          <Visibility fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Download'>
                        <IconButton
                          size='small'
                          className={classes.imageActionButton}
                          onClick={e => {
                            e.stopPropagation();
                            handleDownload(url, fileName);
                          }}>
                          <GetApp fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {otherAttachments.length > 0 && (
          <>
            {imageAttachments.length > 0 && (
              <div
                className={classes.attachmentsTitle}
                style={{ marginTop: "12px" }}>
                <AttachFile fontSize='small' />
                Documents ({otherAttachments.length})
              </div>
            )}
            <div className={classes.attachmentsList}>
              {otherAttachments.map((url, index) => {
                const fileName = getFileNameFromUrl(url);
                const fileIcon = getFileIcon(url);

                return (
                  <Chip
                    key={index}
                    icon={fileIcon}
                    label={fileName}
                    className={classes.attachmentChip}
                    onClick={() => window.open(url, "_blank")}
                    onDelete={() => handleDownload(url, fileName)}
                    deleteIcon={
                      <Tooltip title='Download'>
                        <GetApp fontSize='small' />
                      </Tooltip>
                    }
                    variant='outlined'
                    size='small'
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  const handleClose = () => {
    setOpen(false);
    setShowAddInteraction(false);
    setEditingInteraction(null);
    setInteractionContent("");
    setInteractionType(1);
    setInteractionDate("");
    setExpandedThreads({});
    setPreviewImage(null);
  };

  const fetchInteractions = useCallback(async () => {
    if (!prospectData?.id) return;

    setIsLoading(true);
    try {
      const response = await dispatch(
        contactsTypesActions.onGetProspectInteraction({
          prospectId: prospectData.id,
          dateFrom: null,
          dateTo: null,
          pagination: {
            pageSize: 1000,
            pageNo: 0,
            orderByKey: "modifiedOn",
            orderByVal: 0,
          },
        }),
      );

      if (response?.data?.success) {
        setInteractions(response.data.data.data || []);
      } else {
        setInteractions([]);
      }
    } catch (error) {
      console.error("Error fetching interactions:", error);
      toast.error("Failed to load interaction history");
      setInteractions([]);
    } finally {
      setIsLoading(false);
    }
  }, [prospectData?.id, dispatch]);

  const saveInteraction = async () => {
    if (!interactionContent.trim()) {
      toast.error("Please enter interaction details");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        prospectId: prospectData?.id,
        comments: interactionContent.trim(),
        type: interactionType,
        interactionDate: interactionDate || new Date().toISOString(),
        isContacted: true,
      };

      if (editingInteraction) {
        payload.id = editingInteraction.id;
      }

      const response = await dispatch(
        contactsTypesActions.onAddProspectInteraction(payload),
      );

      if (response?.data?.success) {
        setInteractionContent("");
        setInteractionType(1);
        setInteractionDate("");
        setShowAddInteraction(false);
        setEditingInteraction(null);
        toast.success(
          editingInteraction
            ? "Interaction updated successfully"
            : "Interaction saved successfully",
        );
        fetchInteractions();
      } else {
        toast.error(response?.data?.message || "Failed to save interaction");
      }
    } catch (error) {
      console.error("Error saving interaction:", error);
      toast.error("Failed to save interaction");
    } finally {
      setIsSaving(false);
    }
  };

  const scrollToForm = () => {
    setTimeout(() => {
      if (scrollableBodyRef.current && formContainerRef.current) {
        const container = scrollableBodyRef.current;
        container.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleEditInteraction = interaction => {
    setEditingInteraction(interaction);
    setInteractionContent(interaction.comments || "");
    setInteractionType(interaction.type || 1);

    if (interaction.interactionDate) {
      try {
        const date = new Date(interaction.interactionDate);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const formattedDate = `${year}-${month}-${day}`;
          setInteractionDate(formattedDate);
        } else {
          setInteractionDate("");
        }
      } catch (error) {
        console.error("Error parsing interaction date:", error);
        setInteractionDate("");
      }
    } else {
      setInteractionDate("");
    }

    setShowAddInteraction(true);
    scrollToForm();
  };

  const handleAddInteraction = () => {
    setEditingInteraction(null);
    setInteractionContent("");
    setInteractionType(1);
    setInteractionDate("");
    setShowAddInteraction(true);
    scrollToForm();
  };

  const handleCancel = () => {
    setInteractionContent("");
    setInteractionType(1);
    setInteractionDate("");
    setShowAddInteraction(false);
    setEditingInteraction(null);
  };

  const getTodayDate = () => {
    return new Date().toISOString()?.split("T")[0];
  };

  const uploadFroalaImage = file => {
    dispatch(launchesActions.uploadImage(file)).then(res => {
      if (res.success) {
        test = res.data.url;
      }
    });
  };

  const onHandleModelChange = model => {
    let removedEntities = model?.replace(/&nbsp;/g, " ");
    let withoutTags = removedEntities?.replace(/<(?!img\b)[^>]*>/g, "");
    let cleanedStr = withoutTags?.replace(/\s+/g, "").trim();
    if (cleanedStr?.length > 0) {
      setInteractionContent(model);
    } else {
      setInteractionContent("");
    }
  };

  const formatDisplayDate = dateString => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getTypeLabel = typeValue => {
    const type = INTERACTION_TYPES.find(t => t.value === typeValue);
    return type ? type.label : typeValue;
  };

  const extractFirstLine = htmlContent => {
    if (!htmlContent) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    const firstLine = textContent?.split("\n")[0] || textContent;
    return firstLine.length > 100
      ? firstLine.substring(0, 100) + "..."
      : firstLine;
  };

  const toggleThreadExpansion = interactionId => {
    setExpandedThreads(prev => ({
      ...prev,
      [interactionId]: !prev[interactionId],
    }));
  };

  const isThreadExpanded = interactionId => {
    return expandedThreads[interactionId] || false;
  };

  const renderEmailThread = interaction => {
    if (!interaction.threads || interaction.threads.length === 0) {
      return (
        <div className={classes.expandedEmail}>
          <FroalaEditorView model={interaction.comments} />
        </div>
      );
    }

    return (
      <div className={classes.threadContainer}>
        {interaction.threads.map((thread, index) => (
          <div key={index} className={classes.threadItem}>
            <div className={classes.threadHeader}>
              <Typography variant='caption' color='textSecondary'>
                Message {interaction.threads.length - index}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {formatDisplayDate(
                  thread.createdDate || interaction.interactionDate,
                )}
              </Typography>
            </div>
            <div className={classes.threadContent}>
              <FroalaEditorView model={thread.bodyHtml} />

              {thread.attachments &&
                thread.attachments.length > 0 &&
                renderAttachments(thread.attachments)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (open && prospectData?.id) {
      fetchInteractions();
    }
  }, [open, prospectData?.id, fetchInteractions]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      classes={{
        paper: classes.dialogPaper,
      }}
      maxWidth={false}>
      <DialogTitle className={classes.header} disableTypography>
        <Typography variant='h2' className={classes.headerTitle}>
          Interaction History - {prospectData?.firstName}{" "}
          {prospectData?.lastName}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size='small'>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.contentWrapper}>
          <div className={classes.scrollableBody} ref={scrollableBodyRef}>
            <Typography className={classes.modalTitle}>
              Interaction History
            </Typography>

            <Button
              variant='contained'
              color='primary'
              startIcon={<NoteAdd />}
              onClick={handleAddInteraction}
              className={classes.addButton}
              disabled={showAddInteraction}>
              Add New Interaction
            </Button>

            {showAddInteraction && (
              <div className={classes.formContainer} ref={formContainerRef}>
                <div className={classes.formTitle}>
                  {editingInteraction
                    ? "Edit Interaction"
                    : "Add New Interaction"}
                </div>

                <Grid container spacing={2} className={classes.formGrid}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label='Interaction Type'
                      value={interactionType}
                      onChange={e =>
                        setInteractionType(parseInt(e.target.value))
                      }
                      fullWidth
                      variant='outlined'
                      size='small'
                      className={classes.formField}>
                      {INTERACTION_TYPES.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Interaction Date'
                      type='date'
                      value={interactionDate}
                      onChange={e => setInteractionDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        max: getTodayDate(),
                      }}
                      fullWidth
                      variant='outlined'
                      size='small'
                      className={classes.dateField}
                    />
                  </Grid>
                </Grid>

                <div>
                  <div className={classes.sectionTitle}>
                    Interaction Details
                  </div>
                  {interactionType === 2 ? (
                    <FroalaEditor
                      model={interactionContent}
                      onModelChange={onHandleModelChange}
                      tag='textarea'
                      config={{
                        placeholderText:
                          open === true
                            ? "Add Campaign Message..."
                            : "Compose email",
                        events: {
                          "image.inserted": function ($img, response) {
                            $img[0].src =
                              "https://logosbynick.com/wp-content/uploads/2021/01/animated-gif.gif";
                            setTimeout(() => {
                              $img[0].src = test;
                            }, 7000);
                          },
                          "image.beforeUpload": function (images) {
                            uploadFroalaImage(images);
                          },
                        },
                        key: "VCC6kE4A3D3H3C7C7C6cWHNGGDTCWHIg1Ee1Oc2Yc1b1Lg1POkB6B5F5A3F3E3B3F2A5C3==",
                      }}
                    />
                  ) : (
                    <TextareaAutosize
                      className={classes.textArea}
                      value={interactionContent}
                      onChange={e => setInteractionContent(e.target.value)}
                      placeholder='Enter interaction details here...'
                      disabled={isSaving}
                      minRows={3}
                    />
                  )}
                </div>

                <div className={classes.actions}>
                  <Button
                    variant='outlined'
                    onClick={handleCancel}
                    className={classes.cancelButton}
                    disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={saveInteraction}
                    disabled={isSaving || !interactionContent.trim()}>
                    {isSaving ? (
                      <CircularProgress size={16} />
                    ) : editingInteraction ? (
                      "Update Interaction"
                    ) : (
                      "Save Interaction"
                    )}
                  </Button>
                </div>
              </div>
            )}

            <div className={classes.interactionsContainer}>
              {isLoading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress size={40} />
                </div>
              ) : interactions.length > 0 ? (
                interactions.map(interaction => (
                  <div key={interaction.id} className={classes.interactionItem}>
                    <div className={classes.interactionActions}>
                      {interaction.isThreaded ? (
                        <MuiIconButton
                          className={classes.actionButton}
                          onClick={() => toggleThreadExpansion(interaction.id)}
                          size='small'
                          title={
                            isThreadExpanded(interaction.id)
                              ? "Collapse thread"
                              : "View full thread"
                          }>
                          {isThreadExpanded(interaction.id) ? (
                            <VisibilityOff fontSize='small' />
                          ) : (
                            <Visibility fontSize='small' />
                          )}
                        </MuiIconButton>
                      ) : (
                        <MuiIconButton
                          className={classes.actionButton}
                          onClick={() => handleEditInteraction(interaction)}
                          size='small'>
                          <Edit fontSize='small' />
                        </MuiIconButton>
                      )}
                    </div>

                    <div className={classes.interactionContent}>
                      {interaction.type === 2 ? (
                        interaction.isThreaded ? (
                          isThreadExpanded(interaction.id) ? (
                            renderEmailThread(interaction)
                          ) : (
                            <div
                              className={classes.emailPreview}
                              onClick={() =>
                                toggleThreadExpansion(interaction.id)
                              }
                              title='Click to view email thread'>
                              {extractFirstLine(interaction.comments)}
                              {interaction.threads &&
                                interaction.threads.length > 0 && (
                                  <Typography
                                    variant='caption'
                                    display='block'
                                    style={{ marginTop: "4px", color: "#666" }}>
                                    {interaction.threads.length} message(s) in
                                    thread
                                    {interaction.threads.some(
                                      t => t.attachments?.length > 0,
                                    ) && " • Has attachments"}
                                  </Typography>
                                )}
                            </div>
                          )
                        ) : (
                          <FroalaEditorView model={interaction.comments} />
                        )
                      ) : (
                        interaction.comments
                      )}
                    </div>

                    <div className={classes.interactionMeta}>
                      <div>
                        <span className={classes.interactionType}>
                          {getTypeLabel(interaction.type)}
                        </span>
                      </div>
                      <div>
                        {formatDisplayDate(interaction.interactionDate)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={classes.emptyState}>
                  No interactions found. Add your first interaction!
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      {previewImage && (
        <div
          className={classes.imageModal}
          onClick={() => setPreviewImage(null)}>
          <img
            src={previewImage}
            alt='Preview'
            className={classes.imageModalContent}
            onClick={e => e.stopPropagation()}
          />
          <IconButton
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              color: "#fff",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            }}
            onClick={() => setPreviewImage(null)}>
            <Close />
          </IconButton>
        </div>
      )}
    </Dialog>
  );
};

export default InteractionHistoryModal;
